import React from "react"
import Img from "gatsby-image"
import { linkResolver } from "../utils/linkResolver"
import { RichText } from "prismic-reactjs"
import { useStaticQuery, graphql } from "gatsby"
import { useInView } from "react-intersection-observer"
import { motion } from "framer-motion"
import Reveal from "./reveal"
import { OutboundLink } from "gatsby-plugin-google-analytics"

const WhySection = ({ icon, title, subtitle }) => {
  const item = {
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    hide: { opacity: 0, y: 0, transition: { duration: 0.3 } },
  }
  return (
    <motion.div className="flex flex-col items-center" variants={item}>
      <div className="w-8 y-8 mb-2">
        <Img fluid={icon} />
      </div>
      <div className="flex flex-col">
        <div className="font-semibold  text-2xl text-center ">{title}</div>
        <div className="font-normal  text-sm text-center">{subtitle}</div>
      </div>
    </motion.div>
  )
}

const WhySections = ({ whyItems }) => {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  })

  const variants = {
    show: { opacity: 1, transition: { duration: 0.3, staggerChildren: 0.1 } },
    hide: { opacity: 0, transition: { duration: 0.3 } },
  }
  return (
    <motion.div
      variants={variants}
      animate={inView ? "show" : "hide"}
      initial="hide"
      ref={ref}
      className=" text-white grid grid-cols-2 sm:gap-x-0 gap-x-12 sm:flex flex-col sm:flex-row ml-6 sm:space-x-12 lg:space-x-20 self-center lg:mt-12 gap-y-10"
    >
      {whyItems.map(({ icon, subtitle, title }, index) => (
        <WhySection
          icon={icon.fluid}
          title={title.text}
          subtitle={subtitle.text}
          key={index}
        />
      ))}
    </motion.div>
  )
}

const Reason = ({ title, desc, icon }) => {
  const item = {
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    hide: { opacity: 0, y: 0, transition: { duration: 0.3 } },
  }
  return (
    <motion.div
      className="flex flex-col md:px-16 px-2 md:justify-start mb-6 md:mb-12"
      variants={item}
    >
      <div className="mr-3">
        <div className="h-8 w-8 flex-none">
          <Img fluid={icon} />
        </div>
      </div>
      <div>
        <h5 className="text-blue-dark mb-1 md:text-xl lg:text-2xl">{title}</h5>
        <div className="max-w-xl md:max-w-sm lg:max-w-xs">
          <RichText render={desc} linkResolver={linkResolver} />
        </div>
      </div>
    </motion.div>
  )
}

export default function Reasons() {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  })

  const variants = {
    show: { opacity: 1, transition: { duration: 0.3, staggerChildren: 0.1 } },
    hide: { opacity: 0, transition: { duration: 0.3 } },
  }

  const data = useStaticQuery(graphql`
    query ReasonsQuery {
      prismicHomepageBodyWhysections {
        items {
          icon {
            fluid {
              ...GatsbyPrismicImageFluid_noBase64
            }
          }
          subtitle {
            text
          }
          title {
            text
          }
        }
      }
      prismicHomepageBodyReason {
        items {
          icon {
            fluid {
              ...GatsbyPrismicImageFluid_noBase64
            }
          }
          reason_description {
            raw
          }
          reason_title {
            text
          }
        }
      }
      prismicHomepage {
        data {
          schedule_cta {
            text
          }
          why_title {
            text
          }
          why_introduction {
            text
          }
          whyimage {
            fluid {
              ...GatsbyPrismicImageFluid_noBase64
            }
          }
        }
      }
    }
  `)
  const whyImage = data.prismicHomepage.data["whyimage"].fluid
  const scheduleCta = data.prismicHomepage.data["schedule_cta"].text
  const whyTitle = data.prismicHomepage.data["why_title"].text
  const whyPara = data.prismicHomepage.data["why_introduction"].text
  const whyItems = data.prismicHomepageBodyWhysections.items
  return (
    
    <div className=" pt-16 md:pt-24 lg:pt-36 bg-blue-dark antialiased">
      
      <div className="relative z-0 mx-auto flex flex-col pb-16 md:pb-24 lg:pb-36 px-8">
        <div className="max-w-lg mx-auto">
          <h1 className="mb-2 sm:mb-4 text-white text-4xl sm:text-5xl lg:text-6xl text-center md:text-left ">{whyTitle}</h1>
          <Reveal once={false} threshold={.3} del={.05}>
            <p className="text-base text-white max-w-sm text-center mb-10 md:mb-">{whyPara}</p>
            </Reveal>
        </div>
        <WhySections whyItems={whyItems} />
      </div>
      <div className="my-4 md:mb-12 lg:mb-28 relative bg-blue-light z-10 px-4 md:px-8 flex flex-col md:flex-row md:justify-center  py-8 md:py-28">
        <div className="w-80 -mt-1 mb-8 md:mb-0 self-center md:self-stretch lg:self-center">
          <Img fluid={whyImage} />
        </div>
        <div className="flex flex-col">
          <motion.div
            className="flex flex-col mb-auto"
            variants={variants}
            animate={inView ? "show" : "hide"}
            initial="hide"
            ref={ref}
          >
            {data.prismicHomepageBodyReason.items.map(
              ({ reason_description, reason_title, icon }, index) => (
                <Reason
                  icon={icon.fluid}
                  title={reason_title.text}
                  desc={reason_description.raw}
                  key={index}
                />
              )
            )}
          </motion.div>
          <div className=" flex md:pl-16 self-center md:self-stretch">
            <OutboundLink
              rel="noopener nofollow"
              target="_blank"
              href="https://webforms.pipedrive.com/f/1Aj12jymq2lWRkoDMIk5EWpvZZ4H0lnoDDVX6kMxk9vrga8Y72OJLtEEYuuChGw5Z"
              className="hoverShadow text-base font-medium pb-3 pt-2 mr-6 px-7 bg-blue text-white rounded-full"
            >
              {scheduleCta}
            </OutboundLink>
          </div>
        </div>
      </div>
    </div>
  )
}
